import { Vault } from "../libs/storage.js";
import { User } from "./utils/User";
import { Tracking } from "./utils/Tracking";
import { ClickIds } from "./utils/ClickIds";
import { Experiment } from "./utils/Experiment";
import { FunnelParams } from "./utils/FunnelParams";
import { EventForMessages, TagManager } from "./utils/TagManager";
import { ExtraParams } from "./utils/ExtraParams";
import { Utils } from "./utils/Utils";
import { PushNotifications } from "./utils/push_notifications/PushNotifications";
import { Deferred } from "./utils/Deferred.class";
import { ScrubRule } from "./utils/Scrub";
import { InfoproductConfiguration } from "./EventCollectorLoader";
import { WebAnalyticsEventStructure } from "./type/WebAnalyticsEventStructure";
import { LeadCreationStructure } from "./type/LeadCreationStructure";
import { AnalyticsContext } from "./type/AnalyticsContext";
import { SiteGeo } from "./type/SiteGeo";
import type { VslServedContext } from "./type/VslServing";
import { VslServing } from "./utils/VslServing";
import { fetchFirstParty } from "./utils/FirstPartyFetch";
import { reportAppError, reportAppEvent } from "./utils/CoreReport";
import { getAdBlockerResult } from "./utils/BlockerDetect";
import { decorateUrl as buildDecoratedUrl } from "./utils/UrlDecorator";
import { PixelManager } from "./pixels/PixelManager";
import { EventIdentity } from "./pixels/EventIdentity";
import {
  SemanticEvent,
  fromCustomEvent,
  fromSignal,
} from "./pixels/SemanticEvents";
import type { MatchIdentity } from "./pixels/AdvancedMatching";

export { EventCollector, MockEventCollector, DataType, buildIdentity };

const SCHEDULED_EVENT_PREFIX = "calendly_scheduled_event_";
// Flags de dedupe de la analítica de la reserva. Comparten prefijo con las
// agendas (claves ya existentes en los navegadores), así que los lectores de
// agendas los saltan explícitamente.
const SCHEDULED_EVENT_EMITTED_PREFIX = `${SCHEDULED_EVENT_PREFIX}emitted-`;

// Evento de analytics que registra en ClickHouse el Lead disparado en el
// navegador (ver _sendOptinPixelFired). Solo analytics: jamás al dataLayer.
const OPTIN_PIXEL_FIRED_EVENT = "onOptinPixelFired";

// Tope de lo que la navegación del optin espera a que el Lead de navegador
// salga de verdad, contado desde que se disparó (ver navigate()). Es el que
// usa gtag por defecto para sus propios callbacks (`event_timeout`).
const LEAD_PIXEL_MAX_WAIT_MS = 2000;

// Antigüedad máxima del clic de una red para que la navegación del optin
// espere a su Lead: la ventana de atribución por clic más larga con la que
// trabajan las redes (90 días). Sin un clic así la red no puede atribuir la
// conversión a un clic de este navegador, y esperar retrasaría a todos los
// visitantes de la BU por las pocas que atribuye de otra forma.
const LEAD_PIXEL_CLICK_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

// Id de la carga de página. Corre en el constructor del collector, el camino
// del que depende TODO (el lead incluido), así que jamás puede lanzar: si no
// se pudiera acuñar, los eventos saldrían sin la clave, igual que los de un
// bundle anterior a ella.
function mintPageLoadId(): string {
  try {
    return EventIdentity.mint();
  } catch {
    return "";
  }
}

function isScheduledEventKey(key: string | null): key is string {
  return (
    !!key &&
    key.indexOf(SCHEDULED_EVENT_PREFIX) === 0 &&
    key.indexOf(SCHEDULED_EVENT_EMITTED_PREFIX) !== 0
  );
}

interface PreloadDeferredEvent<T extends EventForMessages> {
  dataType: DataType;
  data: T;
  deferred: Deferred<unknown>;
}

interface MockEventCollector {
  preloadDeferredEvents: PreloadDeferredEvent<EventForMessages>[];
  do: (dataType: string, data: unknown) => Promise<unknown>;
  crmInfoproductId: number;
  crmTeamId: number;
  protodealInfoproductId: number;
  protodealBusinessUnitId: number;
  context?: AnalyticsContext | null;
  customData?: Record<string, unknown> | null;
  geo: Promise<SiteGeo | null> | null;
  // Deferred del init (TTH-1728): lo crea el preload mock, el collector real
  // lo toma en el constructor como el suyo y lo resuelve con su instancia al
  // terminar init(). Quien espere `initDeferred.promise` en el mock y quien
  // espere `EventCollector.initPromise` en el real esperan la misma promesa.
  // Las páginas publicadas antes no lo traen: eso lo cubre UNA guarda explícita
  // en el constructor (legacy), no el tipo.
  initDeferred: InitDeferred;
}

// Lo que el collector usa del Deferred del mock (el del mock es un objeto ES5
// con la misma forma que utils/Deferred.class).
interface InitDeferred {
  promise: Promise<EventCollector>;
  resolve: (collector: EventCollector) => void;
}

enum DataType {
  WebAnalytics = "webanalytics",
  Lead = "lead",
}

interface DataTypes {
  [DataType.WebAnalytics]: WebAnalyticsEventStructure;
  [DataType.Lead]: LeadCreationStructure;
}

class EventCollector {
  // Deferred del init: el del preload mock (ver MockEventCollector.initDeferred).
  // Lo comparten todos los intentos de init del bootstrap (src/index.ts), así
  // que NUNCA se rechaza: init() se traga sus errores internos y lo resuelve
  // con la instancia; si el collector no llega, sigue pendiente y cada
  // consumidor pone su propio timeout (Promise.race).
  private readonly _initDeferred: InitDeferred;
  private readonly _initPromise: Promise<EventCollector>;
  // do() antes de init(): se avisa y no se espera (como cuando _initPromise
  // empezaba en null).
  private _initStarted = false;
  // Dedupe en memoria de onScheduledEvent (respaldo del flag persistido en
  // Vault para navegadores con storage bloqueado).
  private _emittedScheduledEventUuids: Set<string> = new Set();
  private _preloadDeferredEvents: PreloadDeferredEvent<EventForMessages>[];
  private _user: User | null = null;
  private _tracking: Tracking | null = null;
  private _clickIds: ClickIds | null = null;
  private _experiment: Experiment | null = null;
  private _funnelParams: FunnelParams | null = null;
  private _extraParams: ExtraParams | null = null;
  private _utils: Utils | null = null;
  private _tagManager: TagManager | null = null;
  private _pixels: PixelManager | null = null;
  // Cuándo se disparó el último Lead de navegador (Date.now()) y la promesa
  // de que sus peticiones han salido; null si ninguno o si ninguna red sabe
  // confirmarlo
  private _leadPixelFiredAt: number | null = null;
  private _leadPixelSent: Promise<void> | null = null;
  private _config: InfoproductConfiguration = null;
  private _crmInfoproductId: number | null = null;
  private _crmTeamId: number | null = null;
  private _protodealInfoproductId: number | null = null;
  private _protodealBusinessUnitId: number | null = null;
  private _context: AnalyticsContext | null = null;
  private _customData: Record<string, string> | null = null;
  // In-flight per-viewer geo promise, carried over from the mock (the SSR geo bootstrap assigns it to
  // EventCollector.geo). KEPT as a promise so it survives the mock->real handoff and consumers can
  // `await EventCollector.geo` the same way before and after init, without re-fetching. Null when
  // unavailable.
  private _geo: Promise<SiteGeo | null> | null = null;
  private _dataTypes: DataTypes = {
    webanalytics: new WebAnalyticsEventStructure(this),
    lead: new LeadCreationStructure(this),
  };
  private _vslServing = new VslServing(this);
  private _emittedEventNames: Set<string> = new Set();
  // Identidad de ESTA carga de página (ver el getter pageLoadId). Se acuña al
  // construir el collector, antes de que exista ningún evento.
  private readonly _pageLoadId: string = mintPageLoadId();

  constructor(mockEventCollector: MockEventCollector) {
    this._initDeferred = EventCollector.initDeferredFrom(mockEventCollector);
    this._initPromise = this._initDeferred.promise;
    this._preloadDeferredEvents =
      mockEventCollector.preloadDeferredEvents || [];
    this._crmInfoproductId = mockEventCollector.crmInfoproductId || null;
    this._crmTeamId = mockEventCollector.crmTeamId || null;
    this._protodealInfoproductId =
      mockEventCollector.protodealInfoproductId || null;
    this._protodealBusinessUnitId =
      mockEventCollector.protodealBusinessUnitId || null;
    this._context = mockEventCollector.context || null;
    this._geo = mockEventCollector.geo || null;
    if (mockEventCollector.customData) {
      this.setCustomData(mockEventCollector.customData);
    }

    if (!this._crmInfoproductId) {
      console.error("No hay crmInfoproductId");
    }

    if (!Number.isInteger(this._crmInfoproductId)) {
      console.error("crmInfoproductId no es un número");
    }

    if (!this._crmTeamId) {
      console.error("No hay crmTeamId");
    }

    if (!Number.isInteger(this._crmTeamId)) {
      console.error("crmTeamId no es un número");
    }

    if (!this._protodealInfoproductId) {
      console.error("No hay protodealInfoproductId");
    }

    if (!Number.isInteger(this._protodealInfoproductId)) {
      console.error("protodealInfoproductId no es un número");
    }

    if (!this._protodealBusinessUnitId) {
      console.error("No hay protodealBusinessUnitId");
    }

    if (!Number.isInteger(this._protodealBusinessUnitId)) {
      console.error("protodealBusinessUnitId no es un número");
    }
  }

  get extraParams(): ExtraParams {
    if (this._extraParams === null) {
      console.error("EventCollector not initialized yet");
    }

    return this._extraParams;
  }

  get utils(): Utils {
    if (this._utils === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._utils;
  }

  /**
   * GUARDA LEGACY — páginas publicadas antes de `initDeferred`.
   *
   * Su HTML estático lleva el preload mock antiguo, sin `initDeferred`, aunque
   * el tipo lo declare obligatorio: para ellas el collector crea su propio
   * Deferred. No lanza nunca (una excepción aquí mataría el init y el lead).
   *
   * TODO(TTH-1728): borrar cuando todas las páginas publicadas se hayan vuelto
   * a publicar con el mock que trae `initDeferred`.
   */
  private static initDeferredFrom(mock: MockEventCollector): InitDeferred {
    try {
      const candidate = (mock as Partial<MockEventCollector> | null | undefined)?.initDeferred;
      if (
        candidate &&
        candidate.promise &&
        typeof candidate.promise.then === "function" &&
        typeof candidate.resolve === "function"
      ) {
        return candidate;
      }
    } catch {
      // mock raro (getter que lanza…): se trata como antiguo
    }
    return new Deferred<EventCollector>();
  }

  /**
   * Resuelve con esta instancia cuando init() termina; es la misma promesa que
   * `initDeferred.promise` del preload mock. Nunca se rechaza.
   */
  get initPromise(): Promise<EventCollector> {
    return this._initPromise;
  }

  get user(): User {
    if (this._user === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._user;
  }

  get tracking(): Tracking {
    if (this._tracking === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._tracking;
  }

  get experiment(): Experiment {
    if (this._experiment === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._experiment;
  }

  get funnelParams(): FunnelParams {
    if (this._funnelParams === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._funnelParams;
  }

  get clickIds(): ClickIds {
    if (this._clickIds === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._clickIds;
  }

  get vsl(): VslServing {
    return this._vslServing;
  }

  get context(): AnalyticsContext | null {
    return this._context;
  }

  get geo(): Promise<SiteGeo | null> {
    return this._geo ?? Promise.resolve(null);
  }

  get config(): InfoproductConfiguration {
    return this._config;
  }

  get crmInfoproductId(): number {
    return this._crmInfoproductId;
  }

  get crmTeamId(): number {
    return this._crmTeamId;
  }

  get protodealInfoproductId(): number {
    return this._protodealInfoproductId;
  }

  get protodealBusinessUnitId(): number {
    return this._protodealBusinessUnitId;
  }

  get customData(): string | null {
    return this._customData ? JSON.stringify(this._customData) : null;
  }

  // uuidv7 de la carga de página: viaja en `custom_data.page_load_id` de TODOS
  // los eventos de analytics (WebAnalyticsEventStructure) y en nada más — ni en
  // el lead ni en el dataLayer.
  //
  // Es la mitad de la clave con la que se cuentan en ClickHouse los disparos
  // de píxel: `custom_data.pixels` es la lista ACUMULADA de la carga y cada
  // evento lleva una foto de ella, así que un disparo es un par distinto
  // (page_load_id, posición en la lista). Para que el par identifique un
  // disparo, el id tiene que vivir exactamente lo mismo que esa lista: un
  // collector por carga de página, y un PixelManager por collector. Una
  // restauración desde bfcache conserva los dos, así que sigue cuadrando.
  get pageLoadId(): string {
    return this._pageLoadId;
  }

  // Flags de entorno que los eventos de analytics adjuntan a su custom_data
  // (serie 4/8/2026): local_storage = veredicto real del Vault (probe
  // cacheada), ad_blocker = combinación del bait de red a googlesyndication
  // y el bait DOM de filtrado cosmético (ver BlockerDetect.ts) — se omite
  // mientras sea desconocido (null) para no afirmar en falso.
  get environmentFlags(): Record<string, boolean> {
    const flags: Record<string, boolean> = {
      local_storage: Vault.is_localstorage(),
    };
    const adBlocker = getAdBlockerResult();
    if (adBlocker !== null) {
      flags.ad_blocker = adBlocker;
    }
    return flags;
  }

  setCustomData(data: Record<string, unknown>): void {
    const serialized: Record<string, string> = {};
    for (const [key, value] of Object.entries(data)) {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        serialized[key] = String(value);
      } else {
        serialized[key] = JSON.stringify(value);
      }
    }
    this._customData = serialized;
  }

  async init(
    infoProductConfig: InfoproductConfiguration,
    scrubRules: ScrubRule[],
  ): Promise<string> {
    this._initStarted = true;

    try {
      if (typeof infoProductConfig !== "object") {
        console.error("infoProductConfig must be and object");
      }

      this._config = infoProductConfig;
      this._utils = new Utils();
      this._user = new User();
      await this._user.init();
      this._extraParams = new ExtraParams();
      // Snapshot ANTES de Tracking: su constructor reescribe la URL (quita
      // los utm_*) y ClickIds necesita la URL de aterrizaje original
      const entryUrl = window.location.href;
      this._tracking = new Tracking(
        scrubRules,
        this._extraParams.currentExtraParams,
        this._utils,
      );
      // Backend capture definitions arrive INSIDE the config payload
      // (marketing registry -> api-core aggregation -> config-values lambda),
      // already loaded at this point. Built-ins cover their absence.
      this._clickIds = new ClickIds(
        this._tracking,
        this._config?.clickCaptureDefs ?? null,
        entryUrl,
      );
      this._experiment = new Experiment();
      // Después de Tracking y Experiment: también reescribe la URL (quita proto_c y __pfr_force_*)
      this._funnelParams = new FunnelParams();
      this._tracking.getAdditionalDataToMessage();
      this._experiment.getAdditionalDataToMessage();
      this._user.getAdditionalDataToMessage();
      const trackingInfo = this._tracking.getAdditionalDataToMessage();
      const experimentInfo = this._experiment.getAdditionalDataToMessage();
      const userInfo = this._user.getAdditionalDataToMessage();
      const userFormData = this._user.getUserFormData();

      this._tagManager = new TagManager(
        trackingInfo,
        userInfo,
        userFormData,
        experimentInfo,
        this._config.businessUnitConfigValues?.gtag_manager_id,
        this._config.businessUnitConfigValues?.gtm_custom_loaders,
      );
      // Píxeles de navegador JUSTO detrás de TagManager (§6.3 del diseño): el
      // dataLayer ya existe (lo crea el constructor de TagManager) y
      // window.fbq / window.oaiq quedan compartidos con lo que GTM cargue
      // después, que es exactamente lo que hacen sus snippets oficiales.
      this._pixels = this._createPixelManager(userFormData, userInfo);
      this._installCarryParamsListener();
      this._installWebinarStartRelay();

      // [START] PushNotifications
      const buConfig = this._config.businessUnitConfigValues;
      if (
        buConfig !== undefined &&
        buConfig.push_notifications_enabled !== undefined &&
        buConfig.push_notifications_key !== undefined &&
        buConfig.push_notifications_provider !== undefined &&
        Number(buConfig.push_notifications_enabled) === 1
      ) {
        new PushNotifications(
          buConfig.push_notifications_provider,
          buConfig.push_notifications_key,
          this._utils,
        );
      }
      // [END] PushNotifications
    } catch (error) {
      console.log(error);
    }

    this._initDeferred.resolve(this);

    // page_view se dispara EN EL MISMO PUNTO en que se emite onPageView y
    // ANTES que él: así el propio evento de analytics ya lleva en
    // custom_data.pixels lo que se disparó, y comparte su event_id.
    const pageViewEventId = EventIdentity.mint();
    try {
      this._pixels?.fire(SemanticEvent.PageView, pageViewEventId);
    } catch (e) {
      // los píxeles jamás bloquean la analítica
    }

    this.do(DataType.WebAnalytics, {
      event_name: "onPageView",
      event_id: pageViewEventId,
    });

    this._preloadDeferredEvents.forEach(async (deferredEvent) => {
      try {
        const result = await this.do(
          deferredEvent.dataType,
          deferredEvent.data,
        );

        deferredEvent.deferred.resolve(result);
      } catch (error) {
        console.error(
          `Error processing deferred event for ${deferredEvent.dataType}: ${error}`,
        );
        deferredEvent.deferred.reject(error);
      }
    });

    return Promise.resolve("Ready");
  }

  validate(dataType: DataType, data: unknown) {
    console.warn(
      "EventCollector.validate is not implemented yet",
      dataType,
      data,
    );
    return;

    // TODO: Check if this can be removed, this is not implemented in
    // the data types.
    // const errors = this._dataTypes[dataType].dataValidation(data);
    // if (errors == null) return true;
    // else return errors;
  }

  get tagManager(): TagManager {
    if (this._tagManager === null) {
      console.error("EventCollector not initialized yet");
    }
    return this._tagManager;
  }

  // Píxeles de navegador (Meta, ChatGPT Ads...) gobernados por el módulo de
  // Ads. `null` mientras no haya init o cuando la BU no tiene ninguno.
  // Expuesto para el consentimiento: EventCollector.pixels?.setConsent(false).
  get pixels(): PixelManager | null {
    return this._pixels;
  }

  // Middleware: emite un custom event a todos los canales a la vez con el
  // mismo `data`:
  //   - TagManager / GTM (dataLayer)
  //   - DOM (detail del CustomEvent vía dispatchEvent)
  emitCustomEvent = <T>(eventName: string, data: T = {} as T): void => {
    const isFirst = !this._emittedEventNames.has(eventName);
    this._emittedEventNames.add(eventName);

    const enrichedData = { ...(data as object), is_first_occurrence: isFirst } as T;

    // GTM must never block the DOM event: if TagManager failed to initialize
    // (blocked GTM, misconfig) the event still reaches DOM listeners.
    try {
      this._tagManager?.sendCustomEvent(eventName, enrichedData);
    } catch (error) {
      console.warn("EventCollector: TagManager rejected event", eventName, error);
    }

    dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: enrichedData,
      }),
    );

    this._firePixelCustomEvent(eventName);
  };

  // Traducción de las señales que solo llegan por emitCustomEvent (la fecha
  // elegida en el Calendly) a evento semántico de los píxeles. Un id por
  // disparo: no hay copia de servidor con la que deduplicar. Una vez por
  // carga de página (isOncePerPage en el PixelManager).
  private _firePixelCustomEvent = (eventName: string): void => {
    try {
      if (!this._pixels) {
        return;
      }
      const slug = fromCustomEvent(eventName);
      if (!slug) {
        return;
      }
      this._pixels.fire(slug, EventIdentity.mint());
    } catch {
      // los píxeles jamás interfieren con los eventos de la página
    }
  };

  // Persiste en el Vault los datos de una agenda de Calendly. La key combina el
  // uuid del evento y del invitee con prefijos (evt-/inv-) para distinguirlos.
  // Guarda el payload de la lambda ({ scheduledEvent, invitee }) más stored_at
  // (fecha de guardado). TTL de 60 días. Pensado para guardarse en la landing
  // tras agendar y recuperarse en la thank-you.
  saveScheduledEvent = (
    eventUuid: string,
    inviteeUuid: string,
    data: Record<string, unknown>,
  ): boolean => {
    if (!eventUuid || !inviteeUuid) {
      return false;
    }
    const key = `${SCHEDULED_EVENT_PREFIX}evt-${eventUuid}_inv-${inviteeUuid}`;
    const value = {
      ...(data || {}),
      stored_at: new Date().toISOString(),
    };
    const saved = Vault.set(key, value, { expires: "60d" });
    // Fast-follow TTH-1610: además de guardarse para la thank-you, la reserva
    // completada se persiste como evento de analítica. Este método es el
    // embudo común de las DOS capturas (mensaje del widget en la landing y
    // rescate vía lambda en la thank-you), así que emitir aquí cubre ambas.
    this._emitScheduledEventAnalytics(
      `evt-${eventUuid}_inv-${inviteeUuid}`,
      EventIdentity.forSchedule(eventUuid),
      {
        calendly_event_uuid: eventUuid,
        calendly_invitee_uuid: inviteeUuid,
      },
    );
    return saved;
  };

  // Calendario nativo de Protodeal (CONTRATOS.md §10): el botón de calendario
  // del funnel abre /_booking/c/{slug} en un iframe y, con sus mensajes, llama
  // a estos dos métodos para que la analítica sea la MISMA que con Calendly.
  // Un botón con un collector antiguo (sin ellos) sigue funcionando sin la
  // analítica.

  /**
   * El lead elige hora en el calendario nativo (mensaje `slot_selected`). Lo
   * mismo que dispara hoy la fecha elegida en el Calendly: el custom event
   * `onCalendlyDatetimeSelected` (GTM + DOM, con el mismo nombre para no
   * romper las etiquetas existentes) y, a través de él, el píxel
   * `appointment_slot_selected` (uno por página).
   */
  selectNativeSlot = (data: { start?: string; calendar_id?: number | string }): void => {
    try {
      this.emitCustomEvent("onCalendlyDatetimeSelected", {
        page_name: window.location.pathname,
        calendar_provider: "protodeal",
        calendar_id: data && data.calendar_id,
        start: data && data.start,
      });
    } catch (e) {
      // la analítica jamás interfiere con la reserva
    }
  };

  /**
   * Cita confirmada en el calendario nativo (mensaje `booking_confirmed`).
   * Lo mismo que saveScheduledEvent con Calendly, sin llamar a su lambda:
   * guarda la cita para las thank-you (getScheduledEvent*, con la forma
   * { scheduledEvent, invitee } de Calendly para que las páginas que ya la
   * leen sigan funcionando) y emite, una sola vez por cita (mismo dedupe que
   * saveScheduledEvent), el píxel `opportunity_created` y `onScheduledEvent`
   * con el event_id de la cita: EventIdentity.forSchedule(booking_uuid).
   * La clave usa el uuid de la cita como evento e invitee: la thank-you lo
   * encuentra con `?event_uuid=<booking_uuid>` o getScheduledEvent(uuid).
   */
  saveNativeBooking = (data: {
    booking_uuid?: string;
    event_id?: string;
    start?: string;
    end?: string;
    calendar_id?: number | string;
  }): boolean => {
    const bookingUuid = data && typeof data.booking_uuid === "string" ? data.booking_uuid : "";
    if (!bookingUuid) {
      return false;
    }
    // Identidad de la cita (CONTRATOS §6): la derivada del uuid, igual que
    // con Calendly — marketing deriva la misma cadena para la CAPI. Si el
    // event_id recibido no coincide, manda el derivado.
    const eventId = EventIdentity.forSchedule(bookingUuid);
    let saved = false;
    try {
      // Nombre y email del lead: lo que dejó en el funnel (el mensaje de la
      // cita no los trae); best-effort para thank-you que saluden por nombre
      let form: Record<string, string> = {};
      try {
        form = this._user ? this._user.getUserFormData() : {};
      } catch (e) {
        form = {};
      }
      const key = `${SCHEDULED_EVENT_PREFIX}evt-${bookingUuid}_inv-${bookingUuid}`;
      saved = Vault.set(
        key,
        {
          calendar_provider: "protodeal",
          booking_uuid: bookingUuid,
          event_id: eventId,
          calendar_id: data.calendar_id,
          scheduledEvent: {
            uuid: bookingUuid,
            start_time: data.start || "",
            end_time: data.end || "",
            status: "active",
            calendar_provider: "protodeal",
          },
          invitee: {
            uuid: bookingUuid,
            ...(form.user_name ? { name: form.user_name } : {}),
            ...(form.user_email ? { email: form.user_email } : {}),
          },
          stored_at: new Date().toISOString(),
        },
        { expires: "60d" },
      );
    } catch (e) {
      saved = false;
    }
    this._emitScheduledEventAnalytics(`evt-${bookingUuid}_inv-${bookingUuid}`, eventId, {
      calendar_provider: "protodeal",
      booking_uuid: bookingUuid,
      calendar_id: data.calendar_id,
    });
    return saved;
  };

  // Diseño B del fast-follow (13-ago-2026): `onScheduledEvent` lleva en
  // custom_data los uuids de Calendly y el progreso de VSL por grupo. El
  // event uuid es el MISMO que el CRM guarda en la oportunidad
  // (external_opportunity_id_reference, y el de reagendados en
  // external_opportunity_reschedule_reference), así que el cruce
  // agenda↔variante↔decil en ClickHouse es exacto por identificador — sin
  // tocar CRM ni warehouse. El progreso viene del registro persistido
  // (vsl-progress:*): en la thank-you no hay asignaciones vivas y
  // vsl_serving viajaría vacío. Dedupe por uuid con flag en Vault, marcado
  // ANTES de emitir: entre landing y thank-you preferimos perder un reintento
  // a duplicar (la analítica no reintenta justo por eso).
  //
  // `dedupeId` identifica la reserva (evt-<uuid>_inv-<uuid>), `scheduleEventId`
  // es el event_id compartido con el CAPI y `bookingData` lo propio de cada
  // proveedor (uuids de Calendly, o uuid y calendario de la cita nativa).
  private _emitScheduledEventAnalytics = (
    dedupeId: string,
    scheduleEventId: string,
    bookingData: Record<string, unknown>,
  ): void => {
    try {
      // Doble dedupe: flag persistido (cruza páginas) + Set en memoria de
      // respaldo — con storage totalmente muerto (issue 1J) el flag no
      // persiste y una doble llamada en la MISMA página re-emitiría.
      const memoryKey = dedupeId;
      if (this._emittedScheduledEventUuids.has(memoryKey)) {
        return;
      }
      this._emittedScheduledEventUuids.add(memoryKey);
      const dedupeKey = `${SCHEDULED_EVENT_EMITTED_PREFIX}${dedupeId}`;
      if (Vault.get(dedupeKey)) {
        return;
      }
      Vault.set(
        dedupeKey,
        { at: new Date().toISOString() },
        { expires: "60d" },
      );

      const progress = this.vsl.getVslProgressAll();
      const knownGroups = new Set(progress.map((p) => p.vsl_group_id));
      // Grupos servidos en esta página sin registro de progreso todavía
      // (asignados pero sin arrancar el vídeo): también cuentan. Si hay
      // varias instancias del mismo grupo, gana la última — igual que el
      // registro persistido.
      const servedByGroup = new Map<number, VslServedContext>();
      for (const served of this.vsl.servedAssignments) {
        servedByGroup.set(served.vsl_group_id, served);
      }
      for (const served of servedByGroup.values()) {
        if (!knownGroups.has(served.vsl_group_id)) {
          knownGroups.add(served.vsl_group_id);
          progress.push({
            vsl_group_id: served.vsl_group_id,
            vsl_id: served.vsl_id,
            group_snapshot_id: served.group_snapshot_id,
            snapshot_version: served.snapshot_version,
            is_fallback: served.is_fallback,
            max_decile: null,
            updated_at: new Date().toISOString(),
          });
        }
      }

      // Identidad DERIVADA del uuid de la cita (Calendly o nativa): el CRM
      // construye la misma cadena desde
      // opportunity.external_opportunity_id_reference, así que ambas copias
      // comparten id sin tocar nada del legacy. Se dispara solo aquí — donde
      // el dedupe (flag de Vault + Set en memoria) ya ha dicho que esta
      // reserva se emite por primera vez.
      try {
        this._pixels?.fire(SemanticEvent.OpportunityCreated, scheduleEventId);
      } catch (e) {
        // los píxeles jamás interfieren con el guardado de la reserva
      }

      void this.do(DataType.WebAnalytics, {
        event_name: "onScheduledEvent",
        event_id: scheduleEventId,
        page_name: "scheduling",
        custom_data: {
          ...bookingData,
          ...(progress.length > 0
            ? {
                vsl_progress: progress.map((p) => ({
                  vsl_group_id: p.vsl_group_id,
                  vsl_id: p.vsl_id,
                  group_snapshot_id: p.group_snapshot_id,
                  snapshot_version: p.snapshot_version,
                  is_fallback: p.is_fallback,
                  max_decile: p.max_decile,
                })),
              }
            : {}),
        },
      }).catch((e: unknown) => console.log(e));
    } catch (e) {
      // la analítica jamás interfiere con el guardado de la reserva
    }
  };

  // Enumera las keys de scheduled events en localStorage y devuelve sus valores
  // no expirados (Vault.get descarta los expirados). Asume localStorage.
  private _getAllScheduledEventEntries = (): Array<Record<string, unknown>> => {
    const results: Array<Record<string, unknown>> = [];
    // try/catch y no solo typeof: con "cookies y datos" bloqueados (issue 1J)
    // el MERO acceso a window.localStorage lanza SecurityError — typeof
    // evalúa el getter y lanza igual. Mismo patrón que getVslProgressAll.
    try {
      if (typeof localStorage === "undefined") {
        return results;
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!isScheduledEventKey(key)) {
          continue;
        }
        const value = Vault.get(key);
        if (value && value !== false) {
          results.push(value as Record<string, unknown>);
        }
      }
    } catch (e) {
      // sin storage no hay agendas guardadas que devolver
    }
    return results;
  };

  // Búsqueda tipo LIKE: primera entrada cuya key contenga el uuid (event o
  // invitee). Devuelve null si no hay coincidencia.
  private _findScheduledEventByUuid = (
    uuid: string,
  ): Record<string, unknown> | null => {
    // try/catch por el mismo SecurityError de storage bloqueado: devolver
    // null deja que getScheduledEvent caiga al rescate vía lambda.
    try {
      if (!uuid || typeof localStorage === "undefined") {
        return null;
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!isScheduledEventKey(key)) {
          continue;
        }
        if (key.indexOf(uuid) !== -1) {
          const value = Vault.get(key);
          return value && value !== false
            ? (value as Record<string, unknown>)
            : null;
        }
      }
    } catch (e) {
      // sin storage: null → el caller decide (fallback lambda)
    }
    return null;
  };

  // Llama a la lambda para recuperar la agenda, la guarda y la devuelve.
  // Fallback para cuando el redirect ocurrió antes de que la landing terminara
  // de guardar (la respuesta suele venir de la cache de la lambda).
  private _fetchAndStoreScheduledEvent = async (
    eventUuid: string,
    inviteeUuid: string,
  ): Promise<Record<string, unknown> | null> => {
    try {
      const endpoint =
        this.config?.CFG_ENDPOINTS?.CalendlyScheduledEventEndpoint;
      const businessUnitId = this.protodealBusinessUnitId;
      if (!endpoint || !businessUnitId) {
        return null;
      }
      const resp = await fetchFirstParty(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ businessUnitId, eventUuid, inviteeUuid }),
        keepalive: true,
      });
      if (!resp.ok) {
        return null;
      }
      const data = await resp.json();
      if (!data) {
        return null;
      }
      this.saveScheduledEvent(
        eventUuid,
        inviteeUuid,
        data as Record<string, unknown>,
      );
      // Devolver lo recién recibido, NO re-leer del storage: si Vault no
      // pudo guardar (cuota llena, storage bloqueado) la re-lectura daría
      // null aunque la lambda respondió bien.
      return {
        ...(data as Record<string, unknown>),
        stored_at: new Date().toISOString(),
      };
    } catch (e) {
      return null;
    }
  };

  // Recupera una agenda (async). Con uuid: búsqueda LIKE por event o invitee
  // uuid en localStorage. Sin uuid: lee invitee_uuid/event_uuid del query string
  // y hace la misma búsqueda; si no está en localStorage, llama a la lambda como
  // fallback (carrera redirect/respuesta) y la guarda. Devuelve null si no hay
  // params, o si no se encuentra ni se puede recuperar.
  getScheduledEvent = async (
    uuid?: string,
  ): Promise<Record<string, unknown> | null> => {
    if (uuid) {
      return this._findScheduledEventByUuid(uuid);
    }
    const params = new URLSearchParams(window.location.search);
    const eventUuid = params.get("event_uuid") || "";
    const inviteeUuid = params.get("invitee_uuid") || "";
    const lookup = inviteeUuid || eventUuid;
    if (!lookup) {
      return null;
    }
    const found = this._findScheduledEventByUuid(lookup);
    if (found) {
      return found;
    }
    if (!eventUuid || !inviteeUuid) {
      return null;
    }
    return this._fetchAndStoreScheduledEvent(eventUuid, inviteeUuid);
  };

  // Todas las agendas guardadas, ordenadas desc por stored_at.
  getScheduledEventAll = (): Array<Record<string, unknown>> => {
    return this._getAllScheduledEventEntries().sort((a, b) => {
      const aTime = Date.parse((a.stored_at as string) || "") || 0;
      const bTime = Date.parse((b.stored_at as string) || "") || 0;
      return bTime - aTime;
    });
  };

  // La agenda más reciente (por stored_at) o null.
  getScheduledEventLast = (): Record<string, unknown> | null => {
    const all = this.getScheduledEventAll();
    return all.length > 0 ? all[0] : null;
  };

  log() {}

  do = async <T extends EventForMessages>(dataType: DataType, data: T) => {
    if (!this._initStarted) {
      console.error("EventCollector not been called to initialize");
    } else {
      await this._initPromise;
    }

    // Progreso de VSL (fast-follow TTH-1610): los eventos de vídeo del plugin
    // webinar entran todos por aquí — es el sitio donde anotar el decil
    // máximo por grupo sin tocar el plugin (serializado en el contenido
    // publicado). Jamás puede bloquear el envío del evento.
    try {
      if (dataType === DataType.WebAnalytics) {
        const eventName = (data as Record<string, unknown>)["event_name"];
        if (typeof eventName === "string") {
          this.vsl.recordVideoProgress(eventName);
        }
      }
    } catch (e) {
      // el registro de progreso nunca interfiere con la analítica
    }

    // A partir de aquí se trabaja sobre una COPIA. `do()` enriquece el
    // payload (el event_id del optin, el id de la señal de vídeo) y el objeto
    // que llega es del emisor: en el caso del lead, ADEMÁS, es el mismo que
    // acaba en `setUserFormData` (prefill, 365 días). Mutarlo colaba la
    // identidad de UNA conversión concreta en el u_form_data, y de ahí al
    // dataLayer[0] de todas las páginas siguientes y a los tags del proveedor
    // de push — que manda TODAS las claves del prefill.
    const payload = { ...(data as object) } as T;

    // Identidad canónica del optin: se acuña AQUÍ, antes del POST, para que
    // la misma cadena viaje en el mensaje del lead (y de ahí al CAPI), en el
    // dataLayer y en el píxel de navegador. Se reutiliza en los reintentos
    // del POST (el cuerpo se construye una vez) y para el mismo email durante
    // 20 min dentro de la MISMA atribución (ver _leadIdentityScope).
    if (dataType === DataType.Lead) {
      try {
        const leadData = payload as unknown as Record<string, unknown>;
        // Normalizado en el origen: el píxel, el dataLayer y el mensaje tienen
        // que mandar EXACTAMENTE la misma cadena.
        const provided = EventIdentity.normalize(leadData["event_id"]);
        leadData["event_id"] =
          provided !== ""
            ? provided
            : EventIdentity.forLead(
                leadData["user_email"],
                this._leadIdentityScope(),
              );
      } catch (e) {
        // sin identidad el lead sale igual: solo se pierde la dedup
      }
    }

    // Señales de navegador (content_view, video_watched_50): mismo sitio que
    // el progreso de VSL — por aquí pasan TODOS los eventos del plugin de
    // webinar y el relay de onWebinarVideoStart.
    if (dataType === DataType.WebAnalytics) {
      this._firePixelSignal(payload as unknown as Record<string, unknown>);
    }

    return this.save(dataType, payload);
  };

  /**
   * Dimensiones con las que el CRM deduplica el optin (`addUserOptin`:
   * `user_id + tracking_id + experiment_id` dentro de la ventana). El reuso
   * del event_id tiene que ir por las mismas: el mismo email que vuelve dentro
   * de los 20 min con OTRA atribución o en otra variante es un `user_optin`
   * NUEVO en el CRM, y si compartiera identidad con el anterior la red se
   * comería esa segunda conversión (Meta deduplica 48 h por píxel + nombre +
   * id). Solo alimenta un hash, así que su forma exacta da igual mientras sea
   * estable dentro de la página.
   */
  private _leadIdentityScope = (): string => {
    try {
      return JSON.stringify([
        this._tracking?.getAdditionalDataToMessage() ?? {},
        this._experiment?.getAdditionalDataToMessage() ?? {},
      ]);
    } catch (e) {
      // sin contexto, el reuso cae a "solo email": el comportamiento anterior
      return "";
    }
  };

  // Transporte first-party expuesto a los scripts serializados de los plugins
  // de GrapesJS (calendly, webinar...): ellos no pueden importar utils (van
  // incrustados en el HTML publicado), así que llaman a
  // window.EventCollector.fetchFirstParty(url, init) con guard de existencia
  // y fallback a fetch() para collectors antiguos.
  fetchFirstParty = (url: string, init: RequestInit): Promise<Response> => {
    return fetchFirstParty(url, init);
  };

  // Propagación de tracking entre páginas (protoctx v2, 5/8/2026): URL
  // destino + passthrough de la query actual + protoctx fresco (UTMs
  // efectivas + vid). Pura y sin efectos; ante cualquier problema devuelve
  // la URL intacta. Expuesta a plugins y al funnel-router con el mismo
  // patrón de guard que fetchFirstParty.
  decorateUrl = (url: string): string => {
    try {
      return buildDecoratedUrl(url, {
        currentSearch: window.location.search,
        utm: this._tracking!.getAdditionalDataToMessage(),
        visitorId: String(
          this._user!.getAdditionalDataToMessage().visitor_id || "",
        ),
        attributionVia: this._tracking!.attributionVia,
      });
    } catch (e) {
      return url;
    }
  };

  // Navegación con decoración. CONTRATO: navega SIEMPRE — si la decoración
  // falla por lo que sea, redirige con la URL original tal cual. El
  // preload-mock del SSR define un dummy con la misma firma que solo hace el
  // redirect, para que un collector caído nunca deje un botón muerto.
  //
  // Tras un optin, la navegación espera a que el Lead de navegador haya
  // salido de verdad: hay redes que tardan en lanzar su petición (gtag, más
  // de 1 s con la CPU de un móvil) y, si la página se va antes, no sale
  // nunca. Se espera a la confirmación del adaptador y, como mucho, hasta
  // LEAD_PIXEL_MAX_WAIT_MS desde el disparo: el tiempo que ya tardó el intake
  // cuenta, así que un intake lento no añade nada.
  //
  // Solo se espera cuando sirve: el visitante viene de esa red (un clic
  // reciente en el almacén del SDK, ver _cameFrom, o en las cookies de su
  // etiqueta) y el adaptador puede confirmar el envío (en Google, con gtag.js
  // cargado y el consentimiento concedido). El resto — otro botón, un
  // visitante que no viene de Google, uno que no ha aceptado las cookies,
  // gtag bloqueado — navega al instante, como siempre.
  navigate = (url: string): void => {
    let finalUrl = url;
    try {
      finalUrl = this.decorateUrl(url);
    } catch (e) {
      finalUrl = url;
    }
    const sent = this._leadPixelSent;
    const maxWait = this._leadPixelWaitLeft();
    if (sent === null || maxWait <= 0) {
      this._redirect(finalUrl);
      return;
    }
    // Una sola navegación, llegue antes la confirmación o el tope
    let done = false;
    const go = (timedOut: boolean): void => {
      if (done) {
        return;
      }
      done = true;
      if (timedOut) {
        reportAppEvent("lead_pixel_wait_timeout", { app_phase: "pixels" });
      }
      this._redirect(finalUrl);
    };
    try {
      setTimeout(() => go(true), maxWait);
      sent.then(
        () => go(false),
        () => go(false),
      );
    } catch {
      // sin temporizador o sin promesa no hay espera: nunca se queda colgada
      go(false);
    }
  };

  private _redirect = (url: string): void => {
    window.location.href = url;
  };

  // ms que la navegación puede esperar todavía al último Lead de navegador:
  // lo que falta para LEAD_PIXEL_MAX_WAIT_MS desde su disparo, 0 si no hay
  // ninguno o ya ha pasado. Nunca más que ese tope: con un reloj que salta
  // hacia atrás (o cualquier cuenta rara) se espera como si acabara de salir.
  private _leadPixelWaitLeft = (): number => {
    try {
      if (this._leadPixelFiredAt === null) {
        return 0;
      }
      const elapsed = Date.now() - this._leadPixelFiredAt;
      if (!(elapsed >= 0)) {
        return LEAD_PIXEL_MAX_WAIT_MS;
      }
      return Math.max(0, LEAD_PIXEL_MAX_WAIT_MS - elapsed);
    } catch {
      return 0;
    }
  };

  getDomainAndPort = () => {
    return (() => {
      let a = window.location.host.split("."),
        b = a.length;
      return a[b - 2] + "." + a[b - 1];
    })();
  };

  // Construye el orquestador de píxeles con la identidad que YA se conoce al
  // cargar la página: el u_form_data de un optin anterior (mismo dato que
  // TagManager publica en el dataLayer desde hace años) + el visitor_id, que
  // es el external_id que ambas redes usan para casar. Nunca lanza: una BU
  // sin píxeles o un manifest roto dejan `null` y todo sigue igual que hoy.
  private _createPixelManager = (
    userFormData: Record<string, string>,
    userInfo: { visitor_id?: string },
  ): PixelManager | null => {
    try {
      return new PixelManager(this._config?.browserPixels, {
        identity: buildIdentity(userFormData, userInfo?.visitor_id),
      });
    } catch (e) {
      return null;
    }
  };

  /**
   * Reintento único del lead sin `event_id` cuando el intake lo rechaza por
   * validación. Solo para rechazos de CUERPO (400/401/422 — el 401 es el
   * código que usan tanto el lambda como el Ajax::contact del legacy para un
   * payload inválido): un 403/404 es otra cosa y repetir el POST no lo
   * arregla. Devuelve la respuesta solo si el rescate funcionó.
   */
  private _retryLeadWithoutEventId = async (
    url: string,
    message: unknown,
    status: number,
  ): Promise<Response | null> => {
    try {
      if (!RESCUABLE_LEAD_STATUSES[status]) {
        return null;
      }
      const body = message as Record<string, unknown>;
      if (typeof body["event_id"] !== "string") {
        return null;
      }
      const withoutEventId: Record<string, unknown> = { ...body };
      delete withoutEventId["event_id"];

      const retried = await fetchFirstParty(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(withoutEventId),
        keepalive: true,
      });
      reportAppEvent("lead_schema_rejected", {
        app_phase: "save",
        app_status: status,
        app_recovered: retried.ok ? 1 : 0,
      });
      return retried.ok ? retried : null;
    } catch (error) {
      reportAppError(error, {
        app_phase: "save",
        app_type: "lead_event_id_retry",
      });
      return null;
    }
  };

  // Traducción señal -> evento semántico para los eventos de analítica
  // (content_view desde onWebinarVideoStart, video_watched_50 desde el decil
  // 6). Acuña el event_id y lo DEJA en el propio data para que viaje también
  // al dataLayer (TagManager lo respeta) y a custom_data.event_id: navegador
  // y ClickHouse cuentan el mismo evento con el mismo id.
  private _firePixelSignal = (data: Record<string, unknown>): void => {
    try {
      if (!this._pixels) {
        return;
      }
      const slug = fromSignal(data["event_name"]);
      if (!slug) {
        return;
      }
      let eventId =
        typeof data["event_id"] === "string" ? (data["event_id"] as string) : "";
      if (eventId === "") {
        eventId = EventIdentity.mint();
        data["event_id"] = eventId;
      }
      this._pixels.fire(slug, eventId);
    } catch (e) {
      // los píxeles jamás interfieren con la analítica
    }
  };

  // La ÚNICA copia navegador del Lead, con el mismo event_id que llevará la
  // copia servidor del CAPI. Sale con el POST del lead recién lanzado (ver
  // save()), en el mismo momento en que GTM oye `onLeadSubmitSuccess`, y no
  // en su 2xx: el plugin del optin navega en cuanto se resuelve el do() y una
  // red que prepara su envío en asíncrono (gtag hashea el email de las
  // conversiones mejoradas) no llegaba a sacarlo — en octubre de 2026 a
  // Google le llegaba 1 de cada 3 Lead. Disparando aquí el píxel tiene todo
  // lo que tarde el intake, que es lo que siempre tuvo la etiqueta de GTM.
  //
  // El precio, el mismo que paga GTM: también cuenta el envío que luego no se
  // guarda.
  //
  // La identidad recién capturada por el formulario se aporta aquí, pero NO
  // llega a tiempo para este evento ni en Meta ni en OpenAI: Meta ignora un
  // segundo init y el de OpenAI resuelve detrás del track (hash asíncrono).
  // Mejora los eventos posteriores de la misma página; el matching de ESTE
  // evento lo pone la copia servidor del CAPI, que sí lleva la identidad
  // completa. Whop sí la recibe a tiempo (identify síncrono, antes del
  // track). Ver docs-site/docs/modules/pixels.md.
  //
  // Devuelve el event_id con el que salió el Lead, o "" si no salió en
  // ningún píxel (BU sin píxeles, Lead sin mapping...).
  private _firePixelLead = (data: Record<string, unknown>): string => {
    try {
      if (!this._pixels) {
        return "";
      }
      const eventId =
        EventIdentity.normalize(data["event_id"]) ||
        EventIdentity.forLead(data["user_email"], this._leadIdentityScope());
      const fired = this._pixels.fire(
        SemanticEvent.UserOptinCreated,
        eventId,
        buildIdentity(
          data as Record<string, string>,
          String(this._user?.getAdditionalDataToMessage().visitor_id || ""),
        ),
      );
      if (!Array.isArray(fired) || fired.length === 0) {
        return "";
      }
      // navigate() espera a que salgan antes de dejar ir la página, pero solo
      // los de las redes de las que viene el visitante: el manifest es de toda
      // la BU y el Lead sale en todas sus redes en cada optin.
      this._leadPixelFiredAt = Date.now();
      this._leadPixelSent = this._pixels.sendsOfLastFire((network) =>
        this._cameFrom(network),
      );
      return eventId;
    } catch {
      // el píxel jamás puede afectar al guardado del lead
      return "";
    }
  };

  // ¿Guarda el SDK un clic reciente del visitante en esa red? Ante cualquier
  // duda, no: sin clic no hay espera, que es el comportamiento de siempre.
  private _cameFrom = (network: string): boolean => {
    try {
      return (
        this._clickIds?.hasRecentClick(network, LEAD_PIXEL_CLICK_WINDOW_MS) ===
        true
      );
    } catch {
      return false;
    }
  };

  /**
   * Registra en ClickHouse el Lead que salió del navegador con este optin.
   *
   * `custom_data.pixels` solo viaja en eventos de analytics, y tras el 2xx
   * del lead la página no emite ninguno más (el plugin del optin navega en
   * cuanto se resuelve el do()): sin esto la copia navegador del optin no
   * llegaba nunca a `web_event`. Este evento lleva el event_id del Lead y la
   * lista acumulada de la carga, con el Lead ya dentro.
   *
   * El Lead sale al lanzar el POST del lead, pero esto se manda en su 2xx: un
   * envío que no se guarda dispara el píxel y no deja este registro.
   *
   * Nombre PROPIO a propósito: reutilizar `onSaveLeadSuccess` dispararía dos
   * veces las etiquetas de GTM que lo escuchan. Por lo mismo va SOLO al
   * endpoint de analytics: ni dataLayer, ni evento DOM, ni el
   * `onSaveWebanalytics*` que `save()` emite para el resto.
   *
   * Fire-and-forget: el POST (keepalive, como todos) se lanza SIN esperarlo y
   * antes de que se resuelva la promesa del lead, para que sobreviva a la
   * redirección del plugin. No se reintenta (como toda la analítica: un
   * reintento tras una respuesta perdida contaría doble) y un fallo solo se
   * reporta; nunca llega al lead.
   */
  private _sendOptinPixelFired = (eventId: string): void => {
    const tags = { app_phase: "pixels", app_type: OPTIN_PIXEL_FIRED_EVENT };
    try {
      const structure = this._dataTypes[DataType.WebAnalytics];
      const message = structure.generateMessage({
        event_name: OPTIN_PIXEL_FIRED_EVENT,
        event_id: eventId,
      });
      fetchFirstParty(structure.getEndpoint(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(message),
        keepalive: true,
      })
        .then((response) => {
          if (!response.ok) {
            reportAppError(
              new Error(`optin_pixel_fired_http_${response.status}`),
              tags,
            );
          }
        })
        .catch((error: unknown) => {
          reportAppError(error, tags);
        });
    } catch (error) {
      reportAppError(error, tags);
    }
  };

  // Listener global de propagación (protoctx v2): SOLO actúa sobre anchors
  // marcados explícitamente con `data-carry-params` — el collector corre
  // también en websites y no debe decorar nada que nadie pidió. El atributo
  // lo pone el editor (trait "Propagar tracking") o el funnel-router en los
  // links que reescribe (un salto de funnel lo es por definición). Se decora
  // el href en capture-phase, justo antes de que el navegador navegue —
  // idempotente, así que clicks repetidos no acumulan nada. auxclick cubre el
  // botón central (pestaña nueva).
  private _installCarryParamsListener = (): void => {
    try {
      const handler = (event: Event) => {
        try {
          const node = event.target as Element | null;
          if (!node || typeof node.closest !== "function") {
            return;
          }
          const anchor = node.closest(
            "a[data-carry-params]",
          ) as HTMLAnchorElement | null;
          if (!anchor) {
            return;
          }
          // El checkbox del editor usa el patrón de la casa valueTrue/False
          // como strings: desmarcado deja data-carry-params="false" — eso es
          // NO marcado. (El funnel-router marca con "" y el trait con "true".)
          if (anchor.getAttribute("data-carry-params") === "false") {
            return;
          }
          // Los anchors de scroll interno (#seccion) no son navegaciones:
          // decorarlos los convertiría en recarga completa de página
          // (revisión 6/8/2026)
          const href = anchor.getAttribute("href");
          if (!href || href.charAt(0) === "#" || href.indexOf("javascript:") === 0) {
            return;
          }
          anchor.setAttribute("href", this.decorateUrl(href));
        } catch (e) {
          // jamás interferir con una navegación
        }
      };
      document.addEventListener("click", handler, true);
      document.addEventListener("auxclick", handler, true);
    } catch (e) {
      // sin listener la página funciona igual que hoy
    }
  };

  // TTH-1609: el plugin webinar (script serializado DENTRO del contenido
  // publicado de cada página — cambiarlo exigiría re-guardar cada landing)
  // despacha 'onWebinarVideoStart' solo a GTM/DOM via emitCustomEvent. Sin
  // este relay, el arranque del vídeo — el denominador de la curva de
  // retención por deciles — nunca llega a ClickHouse. Se re-emite como
  // webanalytics con el mismo shape que usan los deciles del propio plugin.
  // El contexto de variante viaja solo (vsl_serving se adjunta en mergeData).
  //
  // Es además el disparador de `content_view` en los píxeles de navegador:
  // el do() de abajo pasa por _firePixelSignal, que traduce
  // onWebinarVideoStart -> content_view (una vez por carga de página). Se
  // hace por ahí y no aquí para que haya UN solo camino y un solo event_id
  // compartido por el evento de ClickHouse y el del píxel.
  private _installWebinarStartRelay = (): void => {
    try {
      addEventListener("onWebinarVideoStart", () => {
        try {
          void this.do(DataType.WebAnalytics, {
            event_name: "onWebinarVideoStart",
            page_name: "webinar",
          }).catch((e: unknown) => console.log(e));
        } catch (e) {
          // la analítica jamás interfiere con la reproducción
        }
      });
    } catch (e) {
      // sin relay, todo funciona como hasta hoy
    }
  };

  private save = async <T extends EventForMessages>(
    dataType: DataType,
    data: T,
  ) => {
    // GTM must never block persistence: with a null/broken TagManager this
    // line used to throw BEFORE the fetch and the lead was lost (stratum 2 of
    // the July 2026 incident).
    try {
      this._tagManager?.sendEventForMessages(dataType, data);
    } catch (error) {
      console.warn("EventCollector: TagManager rejected message", dataType, error);
    }

    const dataTypeFormatted =
      dataType.charAt(0).toUpperCase() + dataType.slice(1);

    try {
      const message = this._dataTypes[dataType].generateMessage(data);
      const url = this._dataTypes[dataType].getEndpoint();

      const body = JSON.stringify(message);
      // keepalive: a webview navigating/closing with the POST in flight cancels
      // it otherwise. Leads retry on transient network/5xx failures; analytics
      // events do NOT (a retry after a lost response would double-count them).
      const maxAttempts = dataType === DataType.Lead ? 3 : 1;
      let response: Response | null = null;
      let lastError: unknown = null;
      // event_id con el que salió el Lead en el navegador ("" si no salió)
      let leadPixelEventId = "";
      let leadPixelFired = false;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        // Reintentos por XHR: hipótesis en medición de que parte de los fallos
        // son específicos del camino de fetch (cuota keepalive, quirks de
        // webview) — el tag app_transport de los reportes dirá si XHR rescata.
        const transport = attempt > 1 ? "xhr" : "fetch";
        try {
          const request = fetchFirstParty(
            url,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body,
              keepalive: true,
            },
            { transport },
          );
          // El Lead de navegador sale UNA vez por envío, con el POST ya en
          // vuelo y sin esperar su respuesta (ver _firePixelLead). Detrás del
          // POST y no delante: el lead no espera a ningún vendor.
          if (dataType === DataType.Lead && !leadPixelFired) {
            leadPixelFired = true;
            leadPixelEventId = this._firePixelLead(
              data as unknown as Record<string, unknown>,
            );
          }
          response = await request;
          lastError = null;
          if (response.status < 500) {
            if (attempt > 1) {
              // señal fiable: el envío entró en un reintento, red ya viva
              reportAppEvent("save_recovered", {
                app_phase: "save",
                app_type: dataType,
                app_attempt: attempt,
                app_transport: transport,
              });
            }
            break;
          }
          reportAppError(new Error(`save_http_${response.status}`), {
            app_phase: "save",
            app_type: dataType,
            app_attempt: attempt,
            app_transport: transport,
          });
        } catch (error) {
          lastError = error;
          response = null;
          reportAppError(error, {
            app_phase: "save",
            app_type: dataType,
            app_attempt: attempt,
            app_transport: transport,
          });
        }
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, attempt * 500));
        }
      }

      if (response === null) {
        throw lastError;
      }

      // Red de seguridad del acoplamiento con el schema del intake. El lead se
      // valida contra el schema VIVO de event-manager-php, que es
      // `additionalProperties: false`: si este bundle llega a producción antes
      // de que el legacy publique `event_id` —o si el legacy hace rollback—,
      // la respuesta es un 4xx de validación que NO se reintenta (solo se
      // reintentan 5xx y errores de red) y el lead se pierde entero, en todos
      // los tenants a la vez. El lead vale infinitamente más que la dedup: se
      // reintenta UNA vez sin el campo (un 4xx no persiste nada, así que el
      // reintento no duplica) y se deja la señal en Sentry.
      //
      // El Lead de navegador ya salió con el event_id: en este rescate el CAPI
      // mandará su copia con otro id (dedup_key) y la red contará las dos. Se
      // asume: solo pasa con el intake desalineado, que ya avisa por Sentry.
      if (!response.ok && dataType === DataType.Lead) {
        const rescued = await this._retryLeadWithoutEventId(
          url,
          message,
          response.status,
        );
        if (rescued !== null) {
          response = rescued;
        }
      }

      if (response.ok) {
        const responseData = await response.json();

        if (dataType === DataType.Lead) {
          this._user.setUserFormData(data as unknown as Record<string, string>);
        }

        this.emitCustomEvent("onSave" + dataTypeFormatted + "Success", {
          ...data,
          response: responseData,
        });

        // Detrás de los callbacks de siempre (GTM y DOM salen igual y en el
        // mismo orden) y antes de resolver: el POST ya está en vuelo cuando el
        // plugin navega. Solo con el lead guardado. Ver _sendOptinPixelFired.
        if (leadPixelEventId !== "") {
          this._sendOptinPixelFired(leadPixelEventId);
        }
        return responseData;
      } else {
        const rawResponse = await response.text();

        this.emitCustomEvent("onSave" + dataTypeFormatted + "Error", {
          ...data,
          rawResponse,
        });
        return "KO save";
      }
    } catch (error) {
      // A network failure is as much a lost save as an HTTP error: emit the
      // same custom event (the old silent console.log hid an entire loss
      // stratum) and report it through the first-party error channel when
      // available (window.Sentry ships via the SSR core-support snippet;
      // late-bound guard — never a hard dependency, lesson from probe v3).
      console.log(error);
      this.emitCustomEvent("onSave" + dataTypeFormatted + "Error", {
        ...data,
        error: String(error),
        networkError: true,
      });
      reportAppError(error, {
        app_phase: "save_exhausted",
        app_type: dataType,
      });
      return "KO error";
    }
  };
}

// Identidad del visitante para el advanced matching de los píxeles, a partir
// del shape que ya usan el optin y el u_form_data persistido:
// user_email / user_name / user_phone (+ user_phone_country, que va aparte
// porque el CRM guarda el teléfono SIN prefijo y las redes lo quieren
// completo). El nombre llega en un solo campo: primer token = nombre, resto =
// apellidos, que es lo que hace cualquier integración de estas.
//
// Función libre y no método: no toca estado del collector y así se testea
// sola. Cada adaptador normaliza y hashea según las reglas de SU red
// (AdvancedMatching); aquí solo se recolecta.
// Códigos con los que el intake rechaza el CUERPO del lead (el lambda y el
// Ajax::contact del legacy usan 401 para un payload inválido; 400/422 cubren
// cualquier otro validador por delante).
const RESCUABLE_LEAD_STATUSES: Record<number, boolean> = {
  400: true,
  401: true,
  422: true,
};

function buildIdentity(
  formData: Record<string, string> | null | undefined,
  visitorId?: string | null,
): MatchIdentity {
  try {
    const data = formData || {};
    const fullName = String(data["user_name"] || "").trim();
    const parts = fullName === "" ? [] : fullName.split(/\s+/);
    const phone = String(data["user_phone"] || "").trim();
    const phoneCountry = String(data["user_phone_country"] || "").trim();
    // El schema define `user_phone` SIN prefijo y `user_phone_country` aparte,
    // pero hoy NINGÚN formulario manda el prefijo suelto y el consumer del CRM
    // parsea `user_phone` como E.164. Si el teléfono ya viene con "+", no se
    // le antepone nada: "+34" + "+34600111222" daría "3434600111222", un
    // teléfono inexistente que además pasa el filtro de 8-15 dígitos de OpenAI
    // y se mandaría como advanced matching.
    const fullPhone =
      phone === "" || phoneCountry === "" || phone.charAt(0) === "+"
        ? phone
        : phoneCountry + phone;
    return {
      email: String(data["user_email"] || ""),
      phone: fullPhone,
      firstName: parts.length > 0 ? parts[0] : "",
      lastName: parts.length > 1 ? parts.slice(1).join(" ") : "",
      visitorId: String(visitorId || ""),
    };
  } catch (e) {
    return { visitorId: String(visitorId || "") };
  }
}
